import { useState } from "react";
import { getFlagSvgUrl } from "@/lib/country-flags";

interface NativeFlagProps {
  emoji: string;
  label?: string;
  className?: string;
}

/**
 * Renders a country flag as an SVG image (FlagCDN) instead of relying on
 * platform emoji fonts. iOS Simulator and several Android WebView builds do
 * NOT render regional-indicator flag emoji, so we always render an <img>
 * for true flag emoji and fall back to the text emoji otherwise.
 */
export function NativeFlag({ emoji, label, className = "" }: NativeFlagProps) {
  const [errored, setErrored] = useState(false);

  const safe = emoji || "🌍";
  const cps = [...safe].map((c) => c.codePointAt(0)).filter((c): c is number => !!c);
  const isFlag =
    cps.length === 2 && cps.every((cp) => cp >= 0x1f1e6 && cp <= 0x1f1ff);

  if (isFlag && !errored) {
    return (
      <span
        role="img"
        aria-label={label || "flag"}
        className={`inline-flex shrink-0 items-center justify-center overflow-hidden leading-none ${className}`}
      >
        <img
          src={getFlagSvgUrl(safe)}
          alt={label || "flag"}
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
          className="h-full w-full object-cover rounded-[3px]"
          draggable={false}
        />
      </span>
    );
  }

  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden leading-none ${className}`}
      style={{
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
      }}
    >
      {safe}
    </span>
  );
}
